import { dateFormat } from '@/src/app/utils/date.utils';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { WsHelperService } from '@shared/services/ws-helper.service';
import { AuthService } from '@src/app/features/authentication/services/auth.service';
import { allQaResults, practitioners } from '@utils/routing.utils';
import { switchMap, tap } from 'rxjs/operators';
import { PermissionEnum } from 'src/app/core/permissions/permission.enum';
import { BaseComponent } from 'src/app/shared/components/base-component/base.component';
import { PractitionerDetails, PractitionerNote, PractitionerSpecialization } from '../../models/practitioner.model';
import { PractitionerService } from '../../practitioner.service';
import { PractitionerCreateNoteModalComponent } from '../modals/practitioner-create-note-modal/practitioner-create-note-modal.component';
import {
  DataTableDataProvider,
  GenericDataTableDataProvider
} from '@shared/modules/general-commons/components/data-table/data-table-data-provider';
import { GlobalErrorHandlerService } from '@shared/services/global-error-handler.service';
import { GenericPageComponent } from '@shared/modules/general-commons/components/generic-page/generic-page.component';
import { UpperCasePipe, DatePipe } from '@angular/common';
import { DetailsPageComponent } from '@shared/modules/details-commons/components/details-page/details-page.component';
import { SimpleDetailsValueComponent } from '@shared/modules/details-commons/components/simple-details-value/simple-details-value.component';
import { PermittedDirective } from '@core/permissions/permitted.directive';
import { DataTableComponent } from '@shared/modules/general-commons/components/data-table/data-table.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ValueSeparatorComponent } from '@shared/modules/general-commons/components/value-separator/value-separator.component';
import { EmptyTableStateComponent } from '@shared/components/empty-table-state/empty-table-state.component';
import { DateCustomPipe } from '@shared/modules/general-commons/pipes/date-custom.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  templateUrl: './practitioner-details-page.component.html',
  imports: [
    GenericPageComponent,
    DetailsPageComponent,
    SimpleDetailsValueComponent,
    PermittedDirective,
    RouterLink,
    DataTableComponent,
    MatButton,
    MatIcon,
    ValueSeparatorComponent,
    EmptyTableStateComponent,
    UpperCasePipe,
    DatePipe,
    DateCustomPipe,
    TranslateModule
  ]
})
export class PractitionerDetailsPageComponent extends BaseComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private practitionerService = inject(PractitionerService);
  private changeDetector = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  router = inject(Router);
  private dialog = inject(MatDialog);
  private readonly wsHelperService = inject(WsHelperService);
  private globalErrorHandlerService = inject(GlobalErrorHandlerService);

  readonly permissionEnum = PermissionEnum;
  readonly allQaResults = allQaResults;
  readonly practitioners = practitioners;
  readonly dateFormat = dateFormat;

  practitionerId: number;
  practitioner: PractitionerDetails;
  readonly treatmentCentersTableDataProvider: DataTableDataProvider<any>;
  specializations: PractitionerSpecialization[] = [];
  showLatestCases: boolean;
  notes: PractitionerNote[] = [];
  isSurgeon: boolean;

  constructor() {
    super();
    const practitionerService = this.practitionerService;
    const wsHelperService = this.wsHelperService;
    const globalErrorHandlerService = this.globalErrorHandlerService;

    this.treatmentCentersTableDataProvider = new GenericDataTableDataProvider(
      {
        loadFunction: pageSort =>
          wsHelperService.callWithSpinner(practitionerService.getPractitionerTreatmentCenters(this.practitionerId, pageSort))
      },
      wsHelperService,
      globalErrorHandlerService
    );
  }

  ngOnInit() {
    this.getPractitionerFromParams();
  }

  private getPractitionerFromParams(): void {
    this.subSink.sink = this.route.params
      .pipe(
        tap(params => (this.practitionerId = params.id)),
        switchMap(() =>
          this.wsHelperService.callWithSpinner(this.practitionerService.getPractitionerDetails(this.practitionerId), {
            redirectOn404StatusCode: true
          })
        )
      )
      .subscribe(practitioner => {
        this.practitioner = practitioner;
        this.treatmentCentersTableDataProvider.reload();
        this.loadNotes();
        this.displaySpecializations();
      });
  }

  private loadNotes(): void {
    if (this.authService.checkUserPermission(PermissionEnum.READ_PRACTITIONER_NOTE)) {
      this.wsHelperService.callWithSpinner(this.practitionerService.getNotes(this.practitionerId)).subscribe(notes => {
        this.notes = notes.content;
      });
    }
  }

  onNewNoteButtonClicked() {
    this.dialog
      .open(PractitionerCreateNoteModalComponent, { data: { practitionerId: this.practitionerId } })
      .afterClosed()
      .subscribe(() => {
        this.loadNotes();
        this.changeDetector.detectChanges();
      });
  }

  displaySpecializations(): void {
    let specializationTemp: PractitionerSpecialization;
    this.practitioner.practitionerSpecialization.forEach(specialization => {
      specializationTemp = specialization;
      switch (specialization.specialization) {
        case 'SURGEON':
          specializationTemp.specialization = 'surgeon';
          this.showLatestCases = true;
          this.isSurgeon = true;
          break;
        case 'ORTHODONTIST':
          specializationTemp.specialization = 'practitioner.orthodontist';
          break;
        case 'NUTRITIONIST':
          specializationTemp.specialization = 'practitioner.nutritionist';
          break;
        case 'SPEECH_PROVIDER':
          specializationTemp.specialization = 'practitioner.speech_provider';
          break;
        case 'DENTAL_PROVIDER':
          specializationTemp.specialization = 'practitioner.dental_provider';
          break;
        case 'ENT_PROVIDER':
          specializationTemp.specialization = 'practitioner.ent_provider';
          break;
        case 'PSYCHOSOCIAL_PROVIDER':
          specializationTemp.specialization = 'practitioner.psychosocial_provider';
          break;
        case 'HEARING_SPECIALIST':
          specializationTemp.specialization = 'practitioner.hearing_specialist';
          break;
      }
      this.specializations.push(specializationTemp);
    });
    this.specializations.reverse();
    this.changeDetector.detectChanges();
  }
}
